/**
 * Cancel order flow state + actions for the tracking page.
 *
 * Owns the confirm-dialog visibility, in-flight flag, and last error.
 * Calls the cancelOrder mutation with the tracking code, and on success
 * dispatches the cancelled status into Redux.
 */

import { useState } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { setOrderStatus } from '@/store/orderSlice'
import { menuWidgetApi } from '@/store/convenienceAccessor'

interface UseCancelOrderFlowArgs {
  trackingCode: string | null | undefined
}

interface UseCancelOrderFlowResult {
  showCancelConfirm: boolean
  isCancelling: boolean
  cancelError: string | null
  showConfirm: () => void
  hideConfirm: () => void
  cancel: () => Promise<void>
}

export function useCancelOrderFlow({
  trackingCode,
}: UseCancelOrderFlowArgs): UseCancelOrderFlowResult {
  const dispatch = useAppDispatch()
  const [addCancelOrder] = menuWidgetApi.cancelOrder.add()

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const showConfirm = () => setShowCancelConfirm(true)
  const hideConfirm = () => {
    setShowCancelConfirm(false)
    setCancelError(null)
  }

  const cancel = async () => {
    if (!trackingCode) return
    setIsCancelling(true)
    setCancelError(null)

    try {
      await addCancelOrder({ trackingCode }).unwrap()
      dispatch(setOrderStatus('cancelled'))
      setShowCancelConfirm(false)
    } catch (err: unknown) {
      const msg = (err as { data?: { error?: string } })?.data?.error || 'Failed to cancel order'
      setCancelError(msg)
    } finally {
      setIsCancelling(false)
    }
  }

  return {
    showCancelConfirm,
    isCancelling,
    cancelError,
    showConfirm,
    hideConfirm,
    cancel,
  }
}
